import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Plus, CalendarIcon, AlertCircle, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { FlightStatus } from '@/types/flight';

interface Organization {
  id: string;
  name: string;
  code: string;
}

interface City {
  id: string;
  name: string;
  code: string;
}

interface AircraftType {
  id: string;
  name: string;
  code: string;
}

const FLIGHT_STATUSES: FlightStatus[] = [
  'Scheduled',
  'Check-in Open',
  'Check-in Closed',
  'Boarding Soon',
  'Boarding',
  'Gate Closed',
  'On Time',
  'Delayed',
  'Departed',
  'In Air',
  'Diverted',
  'Recovery',
  'Landed',
  'Arrived',
  'Cancelled'
];

export function CreateFlightDialog({ onFlightCreated }: { onFlightCreated?: () => void }) {
  const { profile } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Form state
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [flightNumber, setFlightNumber] = useState('');
  const [flightDate, setFlightDate] = useState<Date>();
  const [departureTime, setDepartureTime] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [departureCityId, setDepartureCityId] = useState('');
  const [arrivalCityId, setArrivalCityId] = useState('');
  const [aircraftTypeId, setAircraftTypeId] = useState('');
  const [gate, setGate] = useState('');
  const [status, setStatus] = useState<FlightStatus>('Scheduled');

  // Data state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [aircraftTypes, setAircraftTypes] = useState<AircraftType[]>([]);

  // Validation state
  const [flightNumberExists, setFlightNumberExists] = useState(false);
  const [flightNumberChecked, setFlightNumberChecked] = useState(false);
  const [confirmNewFlight, setConfirmNewFlight] = useState(false);
  const [dateError, setDateError] = useState('');
  const [timeError, setTimeError] = useState('');

  // Fetch data
  useEffect(() => {
    if (open) {
      fetchOrganizations();
      fetchCities();
      fetchAircraftTypes();
    }
  }, [open]);

  const fetchOrganizations = async () => {
    const { data, error } = await supabase
      .from('organizations')
      .select('id, name, code')
      .eq('is_active', true)
      .order('name');
    
    if (!error && data) {
      setOrganizations(data);
    }
  };

  const fetchCities = async () => {
    const { data, error } = await supabase
      .from('cities')
      .select('id, name, code')
      .order('name');
    
    if (!error && data) {
      setCities(data);
    }
  };

  const fetchAircraftTypes = async () => {
    const { data, error } = await supabase
      .from('aircraft_types')
      .select('id, name, code')
      .order('name');
    
    if (!error && data) {
      setAircraftTypes(data);
    }
  };

  const checkFlightNumber = async () => {
    if (!flightNumber || !selectedOrgId || !flightDate) return;

    setFlightNumberChecked(false);
    setFlightNumberExists(false);

    const dateStr = format(flightDate, 'yyyy-MM-dd');
    const { data, error } = await supabase
      .from('flights')
      .select('*')
      .eq('organization_id', selectedOrgId)
      .eq('flight_number', flightNumber)
      .gte('scheduled_departure', `${dateStr}T00:00:00`)
      .lte('scheduled_departure', `${dateStr}T23:59:59`)
      .maybeSingle();

    setFlightNumberChecked(true);

    if (data) {
      setFlightNumberExists(true);
      toast.error('Flight number already exists for this date');
    } else {
      setFlightNumberExists(false);
    }
  };

  const validateDate = (date: Date | undefined) => {
    if (!date) {
      setDateError('Date is required');
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      setDateError('Flight date must be today or future');
      return false;
    }

    setDateError('');
    return true;
  };

  const validateTimes = () => {
    if (!departureTime || !arrivalTime) {
      setTimeError('Both times are required');
      return false;
    }

    const [depHour, depMin] = departureTime.split(':').map(Number);
    const [arrHour, arrMin] = arrivalTime.split(':').map(Number);

    const depMinutes = depHour * 60 + depMin;
    const arrMinutes = arrHour * 60 + arrMin;

    if (arrMinutes <= depMinutes) {
      setTimeError('Arrival time must be after departure time');
      return false;
    }

    setTimeError('');
    return true;
  };

  const canProceedToConfirmation = () => {
    return (
      selectedOrgId &&
      flightNumber &&
      flightDate &&
      departureTime &&
      arrivalTime &&
      departureCityId &&
      arrivalCityId &&
      aircraftTypeId &&
      status &&
      flightNumberChecked &&
      !flightNumberExists &&
      (!flightNumberExists || confirmNewFlight) &&
      !dateError &&
      !timeError
    );
  };

  const handleCreateFlight = async () => {
    if (!profile?.id || !flightDate) return;

    setLoading(true);

    try {
      const scheduledDeparture = new Date(flightDate);
      const [depHour, depMin] = departureTime.split(':').map(Number);
      scheduledDeparture.setHours(depHour, depMin, 0, 0);

      const scheduledArrival = new Date(flightDate);
      const [arrHour, arrMin] = arrivalTime.split(':').map(Number);
      scheduledArrival.setHours(arrHour, arrMin, 0, 0);

      const { data, error } = await supabase
        .from('flights')
        .insert({
          organization_id: selectedOrgId,
          flight_number: flightNumber,
          scheduled_departure: scheduledDeparture.toISOString(),
          scheduled_arrival: scheduledArrival.toISOString(),
          departure_city_id: departureCityId,
          arrival_city_id: arrivalCityId,
          aircraft_type_id: aircraftTypeId,
          gate: gate || null,
          status: status,
          created_by: profile.id,
          updated_by: profile.id,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success(`Flight ${flightNumber} created successfully`);
      
      // Reset form
      setOpen(false);
      setShowConfirmation(false);
      resetForm();
      
      if (onFlightCreated) {
        onFlightCreated();
      }
    } catch (error: any) {
      console.error('Error creating flight:', error);
      toast.error(`Failed to create flight: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedOrgId('');
    setFlightNumber('');
    setFlightDate(undefined);
    setDepartureTime('');
    setArrivalTime('');
    setDepartureCityId('');
    setArrivalCityId('');
    setAircraftTypeId('');
    setGate('');
    setStatus('Scheduled');
    setFlightNumberExists(false);
    setFlightNumberChecked(false);
    setConfirmNewFlight(false);
    setDateError('');
    setTimeError('');
  };

  const selectedOrg = organizations.find(o => o.id === selectedOrgId);
  const departureCity = cities.find(c => c.id === departureCityId);
  const arrivalCity = cities.find(c => c.id === arrivalCityId);
  const aircraftType = aircraftTypes.find(a => a.id === aircraftTypeId);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Add New Flight
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {!showConfirmation ? (
          <>
            <DialogHeader>
              <DialogTitle>Create New Flight</DialogTitle>
              <DialogDescription>
                Fill in all flight details. All fields are required unless marked optional.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Step 1: Organization */}
              <div className="space-y-2">
                <Label htmlFor="organization">Organization *</Label>
                <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                  <SelectTrigger id="organization">
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name} ({org.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Step 2: Flight Number */}
              <div className="space-y-2">
                <Label htmlFor="flight-number">Flight Number *</Label>
                <Input
                  id="flight-number"
                  value={flightNumber}
                  onChange={(e) => {
                    setFlightNumber(e.target.value.toUpperCase());
                    setFlightNumberChecked(false);
                  }}
                  onBlur={checkFlightNumber}
                  placeholder="e.g., PX392"
                  disabled={!selectedOrgId}
                />
                {flightNumberChecked && !flightNumberExists && flightNumber && (
                  <Alert className="border-green-500">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <AlertDescription className="text-green-700">
                      New flight number - please confirm to create
                    </AlertDescription>
                  </Alert>
                )}
                {flightNumberExists && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Flight number already exists for this date
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Step 3: Date */}
              <div className="space-y-2">
                <Label>Flight Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !flightDate && 'text-muted-foreground'
                      )}
                      disabled={!selectedOrgId || !flightNumber}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {flightDate ? format(flightDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={flightDate}
                      onSelect={(date) => {
                        setFlightDate(date);
                        validateDate(date);
                        if (date) checkFlightNumber();
                      }}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                {dateError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{dateError}</AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Step 4: Times */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="departure-time">Departure Time *</Label>
                  <Input
                    id="departure-time"
                    type="time"
                    value={departureTime}
                    onChange={(e) => setDepartureTime(e.target.value)}
                    onBlur={validateTimes}
                    disabled={!flightDate}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="arrival-time">Arrival Time *</Label>
                  <Input
                    id="arrival-time"
                    type="time"
                    value={arrivalTime}
                    onChange={(e) => setArrivalTime(e.target.value)}
                    onBlur={validateTimes}
                    disabled={!flightDate}
                  />
                </div>
              </div>
              {timeError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{timeError}</AlertDescription>
                </Alert>
              )}

              {/* Step 5: Cities */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="departure-city">Departure City *</Label>
                  <Select value={departureCityId} onValueChange={setDepartureCityId}>
                    <SelectTrigger id="departure-city">
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map(city => (
                        <SelectItem key={city.id} value={city.id}>
                          {city.name} ({city.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="arrival-city">Arrival City *</Label>
                  <Select value={arrivalCityId} onValueChange={setArrivalCityId}>
                    <SelectTrigger id="arrival-city">
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map(city => (
                        <SelectItem key={city.id} value={city.id}>
                          {city.name} ({city.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Step 6: Additional Details */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="aircraft-type">Aircraft Type *</Label>
                  <Select value={aircraftTypeId} onValueChange={setAircraftTypeId}>
                    <SelectTrigger id="aircraft-type">
                      <SelectValue placeholder="Select aircraft type" />
                    </SelectTrigger>
                    <SelectContent>
                      {aircraftTypes.map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name} ({type.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gate">Gate (Optional)</Label>
                  <Input
                    id="gate"
                    value={gate}
                    onChange={(e) => setGate(e.target.value.toUpperCase())}
                    placeholder="e.g., A12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status *</Label>
                  <Select value={status} onValueChange={(v) => setStatus(v as FlightStatus)}>
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FLIGHT_STATUSES.map(s => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => setShowConfirmation(true)}
                  disabled={!canProceedToConfirmation()}
                >
                  Review & Create
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Confirm Flight Creation</DialogTitle>
              <DialogDescription>
                Please review the flight details before creating.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Organization:</span>
                  <span>{selectedOrg?.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Flight Number:</span>
                  <span className="flex items-center gap-2">
                    {flightNumber}
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      NEW
                    </Badge>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Date:</span>
                  <span>{flightDate && format(flightDate, 'PPP')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Departure:</span>
                  <span>{departureCity?.name} ({departureCity?.code}) at {departureTime}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Arrival:</span>
                  <span>{arrivalCity?.name} ({arrivalCity?.code}) at {arrivalTime}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Aircraft Type:</span>
                  <span>{aircraftType?.name} ({aircraftType?.code})</span>
                </div>
                {gate && (
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Gate:</span>
                    <span>{gate}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="font-medium">Status:</span>
                  <span>{status}</span>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowConfirmation(false)}>
                  Back to Edit
                </Button>
                <Button onClick={handleCreateFlight} disabled={loading}>
                  {loading ? 'Creating...' : 'Confirm & Create'}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
